'use client';

import { useEffect, useRef, useState } from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import { IconCheck, IconCross } from '@/components/icons';
import { PortableTextRenderer } from '@/components/content';
import { cn } from '@/lib/utils/tw-merge';
import { useDictionary } from '@/components/providers';

/** Success auto-dismisses after this delay; the progress bar fills over the
 *  same duration so both stay in sync from a single source of truth. */
export const FORM_FEEDBACK_AUTO_DISMISS_MS = 3000;

type FormFeedbackVariant = 'success' | 'error';

/** Result panel that replaces the form after submit: a status icon, the
 *  CMS-authored (or default) message and a dismiss button. On success an
 *  auto-dismiss progress bar fills over FORM_FEEDBACK_AUTO_DISMISS_MS and then
 *  triggers onDismiss; error panels stay until the user acts. */
export function FormFeedback({
    variant,
    content,
    buttonLabel,
    onDismiss,
    autoDismiss = false,
}: {
    variant: FormFeedbackVariant;
    content?: PortableTextBlock[];
    buttonLabel: string;
    onDismiss: () => void;
    autoDismiss?: boolean;
}) {
    const { form } = useDictionary();
    const [filled, setFilled] = useState(false);

    // Bind the timer to autoDismiss only; read the latest onDismiss through a ref
    // so a changed callback identity can't restart the countdown mid-fill.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        onDismissRef.current = onDismiss;
    });

    useEffect(() => {
        if (!autoDismiss) return;
        const raf = requestAnimationFrame(() => setFilled(true));
        const timer = setTimeout(() => onDismissRef.current(), FORM_FEEDBACK_AUTO_DISMISS_MS);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [autoDismiss]);

    // Fallback for a block with no CMS-authored success/error richText.
    const fallback = form.feedback[variant];

    return (
        <div
            className={cn('form-feedback', `form-feedback--${variant}`)}
            role={variant === 'error' ? 'alert' : 'status'}
        >
            <div className={cn('form-feedback-icon', `form-feedback-icon-${variant}`)}>
                {variant === 'success' ? <IconCheck className="size-7" /> : <IconCross className="size-7" />}
            </div>

            {content?.length ? (
                <PortableTextRenderer value={content} className="form-feedback-text" />
            ) : (
                <div className="form-feedback-text text-center">
                    <p className="form-feedback-title">{fallback.title}</p>
                    <p>{fallback.body}</p>
                </div>
            )}

            <button type="button" className="btn btn-outline mt-8" onClick={onDismiss}>
                {buttonLabel}
            </button>

            {autoDismiss && (
                <div className="form-feedback-progress" aria-hidden="true">
                    <div
                        className="form-feedback-progress-fill"
                        style={{
                            width: filled ? '100%' : '0%',
                            transitionDuration: `${FORM_FEEDBACK_AUTO_DISMISS_MS}ms`,
                        }}
                    />
                </div>
            )}
        </div>
    );
}
